import { Component, Input, OnChanges, OnInit, OnDestroy, Output, EventEmitter, SimpleChanges, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';
import { LazyScrollableDataSource } from '@rappi/common';
import { SelectComponent } from '@rappi/ui/select';
import { AlertsType } from '@rappi/ui/alerts';

import { MakersFinancesMgrService } from '../../../../services/makers-finances-mgr.service';
import { FiltersService } from '../../../../../permissions/services/filters.service';
import { StoreAuxService } from '../../../../../services/storeAux/store-aux.service';
import { AlertsService } from '../../../../../services/alerts/alerts.service';
import { HasPermissionsService } from '../../../../../shared/services/has-permissions/has-permissions.service';
import { CatalogItem, NewPurchaseOrderConfig, PurchaseOrderDetail } from '../../../../models/makers-finances-mgr.models';
import { environment } from '../../../../../../environments/environment';
import {
  DETAIL_MODE, DetailMode, SourceType, PO_FIELD, PO_FIELD_LABELS, PO_SECTION_LABELS, PO_SECTION,
  PO_OWNER_ERROR, PO_STATUS, PO_STATUS_LABELS, BUSINESS_UNIT, BUSINESS_UNIT_LABEL, BusinessUnit,
} from '../../purchase-order.constants';
import { SelectOption, staticSourceFromCatalog } from '../section-base.types';

@Component({
  selector: 'app-section-general-info',
  templateUrl: './section-general-info.component.html',
  styleUrls: ['./section-general-info.component.scss'],
})
export class SectionGeneralInfoComponent implements OnInit, OnChanges, OnDestroy {
  /* istanbul ignore next -- decorator metadata */
  @Input() form: FormGroup;
  /* istanbul ignore next -- decorator metadata */
  @Input() mode: DetailMode;
  /* istanbul ignore next -- decorator metadata */
  @Input() sourceType: SourceType;
  /* istanbul ignore next -- decorator metadata */
  @Input() remoteConfig: NewPurchaseOrderConfig | null;
  /* istanbul ignore next -- decorator metadata */
  @Input() sourceTypeCatalog: CatalogItem[] = [];
  /* istanbul ignore next -- decorator metadata */
  @Input() poTypeCatalog: CatalogItem[] = [];
  /* istanbul ignore next -- decorator metadata */
  @Input() catalogsLoaded = false;
  /* istanbul ignore next -- decorator metadata */
  @Input() submitted = false;
  /* istanbul ignore next -- decorator metadata */
  @Input() fieldConfigs: Record<string, any> = {};
  /* istanbul ignore next -- decorator metadata */
  @Input() lockedSourceType: SourceType | null = null;
  // Flips true once the parent has patched the form from the loaded PO; triggers setSelection on
  // the selects (one-select is not form-bound).
  /* istanbul ignore next -- decorator metadata */
  @Input() poLoaded = false;
  // The loaded PO — used to read the persisted stage for businessUnitSelectDisabled.
  /* istanbul ignore next -- decorator metadata */
  @Input() poDetail: PurchaseOrderDetail | null = null;
  // CMS rollout flag (FinancesConfig.po_business_unit_enabled). While false, the Business Unit
  // field is hidden and the execution selector always shows Maker to execute (legacy behavior).
  /* istanbul ignore next -- decorator metadata */
  @Input() poBusinessUnitEnabled = false;

  // In edit mode the skeleton stays until the PO detail is also loaded (poLoaded), not just the
  // catalogs. In create mode only catalogsLoaded matters.
  get showSkeleton(): boolean {
    return !this.catalogsLoaded || (this.mode === DETAIL_MODE.EDIT && !this.poLoaded);
  }

  readonly F = PO_FIELD;
  readonly LABELS = PO_FIELD_LABELS;
  readonly sectionLabel = PO_SECTION_LABELS[PO_SECTION.GENERAL_INFORMATION];

  readonly selectConfig = { search: false, includeAll: false, showTotalsChip: false, maxNumberOfChips: 1, panelClass: '' };
  readonly searchSelectConfig = { search: true, includeAll: false, showTotalsChip: false, maxNumberOfChips: 1, panelClass: '' };
  readonly makerSelectConfig = { search: true, includeAll: false, showTotalsChip: false, maxNumberOfChips: 1, panelClass: '' };

  /* istanbul ignore next -- decorator metadata */
  @ViewChild('ownerSelect') ownerSelect: SelectComponent<SelectOption>;
  /* istanbul ignore next -- decorator metadata */
  @ViewChild('sourceTypeSelect') sourceTypeSelect: SelectComponent<SelectOption>;
  /* istanbul ignore next -- decorator metadata */
  @ViewChild('makerSelect') makerSelect: SelectComponent<SelectOption>;
  /* istanbul ignore next -- decorator metadata */
  @ViewChild('poTypeSelect') poTypeSelect: SelectComponent<SelectOption>;
  /* istanbul ignore next -- decorator metadata */
  @ViewChild('businessUnitSelect') businessUnitSelect: SelectComponent<SelectOption>;
  /* istanbul ignore next -- decorator metadata */
  @ViewChild('brandGroupSelect') brandGroupSelect: SelectComponent<SelectOption>;

  selectSources: Record<string, LazyScrollableDataSource<SelectOption>> = {};
  private userEmail = '';
  // Keyed by brand group id (string) → plain name, populated as pages are fetched. The dropdown
  // displays "id - name" (per backend's suggested format) but the persisted brand_group_to_execute_name
  // must stay the plain name, so it can't be parsed back out of the displayed label.
  private readonly brandGroupNameById = new Map<string, string>();

  // Owner validation state — drives the red banner and red border on the owner field.
  ownerInvalid = false;
  ownerValidating = false;
  ownerBannerMessage: string | null = null;

  @Output() ownerValidChange = new EventEmitter<boolean>();

  private readonly destroy$ = new Subject<void>();

  /* istanbul ignore next -- decorator metadata */
  constructor(
    private readonly financesService: MakersFinancesMgrService,
    private readonly filtersService: FiltersService,
    private readonly storeAux: StoreAuxService,
    private readonly alerts: AlertsService,
    private readonly permissionsService: HasPermissionsService,
  ) {}

  ngOnInit(): void {
    this.userEmail = localStorage.getItem('email') || '';

    this.selectSources[PO_FIELD.MAKER_TO_EXECUTE] = this._buildMakerToExecuteSource();

    this.selectSources[PO_FIELD.BUSINESS_UNIT] = new LazyScrollableDataSource<SelectOption>(() =>
      of(this.businessUnitOptions.map(o => ({ id: o.value, name: o.label })))
    );

    this.selectSources[PO_FIELD.BRAND_GROUP_TO_EXECUTE_ID] = this._buildBrandGroupToExecuteSource();

    this.selectSources[PO_FIELD.OWNER] = new LazyScrollableDataSource<SelectOption>(
      (page, limit, search) =>
        this.financesService.searchOwners(this.storeAux.country, page + 1, limit, search || undefined)
          .pipe(map(res => res.data.records.map(o => ({ id: o.email, name: o.email }))))
    );

    this.form.get(PO_FIELD.OWNER)?.valueChanges.pipe(
      debounceTime(600),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe(value => this._validateOwner(value));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Factories for the two swap-field data sources, re-invoked (not just built once in ngOnInit) every
  // time the Business Unit toggles — *ngIf destroys the hidden one-select's cdk-virtual-scroll-viewport,
  // which calls disconnect() on its bound LazyScrollableDataSource and permanently completes its
  // internal streams. Reusing that same (now-disconnected) instance when the field reappears renders
  // a blank dropdown, so onBusinessUnitChange rebuilds both fresh on every actual change.
  private _buildMakerToExecuteSource(): LazyScrollableDataSource<SelectOption> {
    return new LazyScrollableDataSource<SelectOption>(
      (page, limit, search) =>
        this.filtersService.getEntities(
          this.filtersService.getBody(environment.modules.financialHub, 'maker'),
          this.filtersService.getParams(page + 1, limit, search)
        ).pipe(map(res => res.data.records.map(e => ({ id: String(e.reference_code || e.id), name: e.name }))))
    );
  }

  private _buildBrandGroupToExecuteSource(): LazyScrollableDataSource<SelectOption> {
    return new LazyScrollableDataSource<SelectOption>(
      (page, limit, search) =>
        this.financesService.listBrandGroups(this.storeAux.country, search || undefined, page + 1)
          .pipe(map(res => res.data.data.map(bg => {
            this.brandGroupNameById.set(String(bg.id), bg.name);
            return { id: String(bg.id), name: `${bg.id} - ${bg.name}` };
          })))
    );
  }

  private _validateOwner(email: string | null): void {
    if (!email) {
      this.ownerInvalid = false;
      this.ownerValidating = false;
      this.ownerBannerMessage = null;
      this.ownerValidChange.emit(true);
      return;
    }
    this.ownerValidating = true;
    this.financesService.validateOwner(email).pipe(
      catchError(() => of(null)),
      takeUntil(this.destroy$),
    ).subscribe(res => {
      this.ownerValidating = false;
      if (res?.data && !res.data.valid) {
        this.ownerInvalid = true;
        const template = this.remoteConfig?.rappi_owner?.banner?.missing_in_zoho ?? PO_OWNER_ERROR.BANNER;
        this.ownerBannerMessage = template.replace('{{owner_email}}', email);
        this.alerts.openAlerts(this.ownerBannerMessage, AlertsType.error);
        this.ownerValidChange.emit(false);
      } else {
        this.ownerInvalid = false;
        this.ownerBannerMessage = null;
        this.ownerValidChange.emit(true);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this._handleCatalogsLoadedChange(changes);
    this._handleSourceTypeCatalogChange(changes);
    this._handlePoTypeCatalogChange(changes);
    this._handleRemoteConfigChange(changes);
    this._handleSourceTypeChange(changes);
    this._handlePoLoadedChange(changes);
  }

  private _handleCatalogsLoadedChange(changes: SimpleChanges): void {
    if (!changes['catalogsLoaded'] || !this.catalogsLoaded) {
      return;
    }
    setTimeout(() => {
      if (this.mode === DETAIL_MODE.CREATE && this.userEmail && this.ownerSelect) {
        this.ownerSelect.setSelection({ id: this.userEmail, name: this.userEmail });
        this.form.get(PO_FIELD.OWNER)?.setValue(this.userEmail);
      }
    });
  }

  private _handleSourceTypeCatalogChange(changes: SimpleChanges): void {
    if (!changes['sourceTypeCatalog'] || !this.sourceTypeCatalog.length) {
      return;
    }
    this.selectSources[PO_FIELD.SOURCE_TYPE] = staticSourceFromCatalog(this.sourceTypeCatalog);
    if (!this.lockedSourceType) {
      return;
    }
    const locked = this.sourceTypeCatalog.find(r => r.code === this.lockedSourceType);
    if (locked) {
      setTimeout(() => {
        this.sourceTypeSelect?.setSelection({ id: locked.code, name: locked.name });
      });
    }
  }

  private _handlePoTypeCatalogChange(changes: SimpleChanges): void {
    if (changes['poTypeCatalog'] && this.poTypeCatalog.length) {
      this.selectSources[PO_FIELD.PO_TYPE] = staticSourceFromCatalog(this.poTypeCatalog);
    }
  }

  // remoteConfig (CMS) typically arrives after ngOnInit already built a fallback datasource from
  // the hardcoded BUSINESS_UNIT/BUSINESS_UNIT_LABEL constants — rebuild it once business_unit_options
  // is available so the CMS-driven values/labels take over.
  private _handleRemoteConfigChange(changes: SimpleChanges): void {
    if (changes['remoteConfig'] && this.remoteConfig?.business_unit_options?.length) {
      this.selectSources[PO_FIELD.BUSINESS_UNIT] = new LazyScrollableDataSource<SelectOption>(() =>
        of(this.businessUnitOptions.map(o => ({ id: o.value, name: o.label })))
      );
    }
  }

  private _handleSourceTypeChange(changes: SimpleChanges): void {
    if (changes['sourceType'] && !changes['sourceType'].firstChange
        && this.isPoTypeVisible && this.poTypeCatalog.length) {
      this.selectSources[PO_FIELD.PO_TYPE] = staticSourceFromCatalog(this.poTypeCatalog);
    }
  }

  private _handlePoLoadedChange(changes: SimpleChanges): void {
    if (changes['poLoaded'] && this.poLoaded) {
      setTimeout(() => {
        this._prefillSelects();
        this._validateOwner(this.form.get(PO_FIELD.OWNER)?.value);
      });
    }
  }

  // Edit mode: sync the select UIs to the patched form values. The form already holds the codes;
  // the selects just need their display set (one-select is not form-bound).
  private _prefillSelects(): void {
    this._prefillSourceTypeSelect();
    this._prefillMakerSelect();
    this._prefillBusinessUnitSelect();
    this._prefillBrandGroupSelect();
    this._prefillPoTypeSelect();
    this._prefillOwnerSelect();
  }

  private _prefillSourceTypeSelect(): void {
    const sourceCode = this.form.get(PO_FIELD.SOURCE_TYPE)?.value;
    if (!sourceCode || !this.sourceTypeSelect) {
      return;
    }
    const item = this.sourceTypeCatalog.find(r => r.code === sourceCode);
    if (item) {
      this.sourceTypeSelect.setSelection({ id: item.code, name: item.name });
    }
  }

  private _prefillMakerSelect(): void {
    const makerCode = this.form.get(PO_FIELD.MAKER_TO_EXECUTE)?.value;
    const makerName = this.form.get(PO_FIELD.MAKER_NAME)?.value;
    if (makerCode && this.makerSelect) {
      this.makerSelect.setSelection({ id: makerCode, name: makerName || makerCode });
    }
  }

  private _prefillBusinessUnitSelect(): void {
    const businessUnitCode = this.form.get(PO_FIELD.BUSINESS_UNIT)?.value as string | null;
    if (!businessUnitCode || !this.businessUnitSelect) {
      return;
    }
    const label = this.businessUnitOptions.find(o => o.value === businessUnitCode)?.label ?? businessUnitCode;
    this.businessUnitSelect.setSelection({ id: businessUnitCode, name: label });
  }

  private _prefillBrandGroupSelect(): void {
    const brandGroupId = this.form.get(PO_FIELD.BRAND_GROUP_TO_EXECUTE_ID)?.value;
    const brandGroupName = this.form.get(PO_FIELD.BRAND_GROUP_TO_EXECUTE_NAME)?.value;
    if (brandGroupId && this.brandGroupSelect) {
      this.brandGroupSelect.setSelection({
        id: String(brandGroupId),
        name: brandGroupName ? `${brandGroupId} - ${brandGroupName}` : String(brandGroupId),
      });
    }
  }

  private _prefillPoTypeSelect(): void {
    const poTypeCode = this.form.get(PO_FIELD.PO_TYPE)?.value;
    if (!poTypeCode || !this.poTypeSelect) {
      return;
    }
    const item = this.poTypeCatalog.find(r => r.code === poTypeCode);
    if (item) {
      this.poTypeSelect.setSelection({ id: item.code, name: item.name });
    }
  }

  private _prefillOwnerSelect(): void {
    const ownerEmail = this.form.get(PO_FIELD.OWNER)?.value;
    if (ownerEmail && this.ownerSelect) {
      this.ownerSelect.setSelection({ id: ownerEmail, name: ownerEmail });
    }
  }

  get isPoTypeVisible(): boolean {
    const cfg = this.fieldConfigs[PO_FIELD.PO_TYPE];
    if (!cfg) {
      return false;
    }
    if (cfg.visible === false) {
      return false;
    }
    if (cfg.visible_by_source) {
      return !!cfg.visible_by_source[this.sourceType];
    }
    return true;
  }

  get isPoTypeDisabled(): boolean {
    return this.mode === DETAIL_MODE.EDIT
      && this.fieldConfigs[PO_FIELD.PO_TYPE]?.editable_by_source?.[this.sourceType] === false;
  }

  get sourceTypeSelectDisabled(): boolean {
    if (this.lockedSourceType) {
      return true;
    }
    if (this.mode === DETAIL_MODE.EDIT
        && this.fieldConfigs[PO_FIELD.SOURCE_TYPE]?.editable_by_source?.[this.sourceType] === false) {
      return true;
    }
    return false;
  }

  // Business Unit is only editable while the PO is in Draft; once submitted it's read-only.
  // In create mode there is no persisted stage yet, so the field stays editable (same rule
  // sourceTypeSelectDisabled/isPoTypeDisabled already apply to their own fields).
  get businessUnitSelectDisabled(): boolean {
    return this.mode === DETAIL_MODE.EDIT
      && this.poDetail?.stage !== PO_STATUS_LABELS[PO_STATUS.DRAFT];
  }

  // Whether the Business Unit field itself renders at all — gated by the CMS rollout flag.
  get isBusinessUnitVisible(): boolean {
    return this.poBusinessUnitEnabled;
  }

  // CMS-driven Business Unit options (new_purchase_order.business_unit_options), falling back to
  // the hardcoded BUSINESS_UNIT/BUSINESS_UNIT_LABEL constants if the CMS hasn't configured them.
  get businessUnitOptions(): { value: string; label: string }[] {
    return this.remoteConfig?.business_unit_options?.length
      ? this.remoteConfig.business_unit_options
      : Object.values(BUSINESS_UNIT).map(code => ({ value: code, label: BUSINESS_UNIT_LABEL[code] }));
  }

  // While the flag is off, the Brand Group selector never applies — always show Maker to execute,
  // regardless of any stray business_unit value loaded from the PO.
  get isMakerToExecuteVisible(): boolean {
    return !this.poBusinessUnitEnabled
      || this.form.get(PO_FIELD.BUSINESS_UNIT)?.value !== BUSINESS_UNIT.RETAILER_ADS;
  }

  get isBrandGroupToExecuteVisible(): boolean {
    return this.poBusinessUnitEnabled
      && this.form.get(PO_FIELD.BUSINESS_UNIT)?.value === BUSINESS_UNIT.RETAILER_ADS;
  }

  get isSelfServiceCampaignIdVisible(): boolean {
    const cfg = this.fieldConfigs[PO_FIELD.SELF_SERVICE_CAMPAIGN_ID];
    if (!cfg) {
      return false;
    }
    if (cfg.visible === false) {
      return false;
    }
    if (cfg.visible_by_source) {
      return !!cfg.visible_by_source[this.sourceType];
    }
    return true;
  }

  get isParentPoVisible(): boolean {
    return this.mode === DETAIL_MODE.EDIT
      && !!this.fieldConfigs[PO_FIELD.PARENT_PO]?.visible_by_source?.[this.sourceType];
  }

  get isParentPoDisabled(): boolean {
    const permission = this.fieldConfigs[PO_FIELD.PARENT_PO]?.editable_by_permission;
    if (!permission) {
      return false;
    }
    return !this.permissionsService.checkPermission(permission);
  }

  onSelectChange(fieldKey: string, value: SelectOption): void {
    this.form.get(fieldKey)?.setValue(value?.id ?? null);
  }

  onMakerChange(value: SelectOption): void {
    this.form.get(PO_FIELD.MAKER_TO_EXECUTE)?.setValue(value?.id ?? null);
    this.form.get(PO_FIELD.MAKER_NAME)?.setValue(value?.name ?? null);
  }

  // Only one execution selector may be populated per Business Unit — switching BU clears whichever
  // one no longer applies, both in the form (source of truth for the save payload) and in the
  // one-select UI (not form-bound, so clearSelection() must be called explicitly).
  //
  // one-select's setSelection() unconditionally emits selectionChanged (see
  // ngx-frontend/libs/ui/select/components/select.component.ts), so _prefillSelects() calling
  // businessUnitSelect.setSelection(...) to restore the display in edit mode re-enters this handler
  // with the *same* value already in the form. Without the no-op guard below, that reentrant call
  // would null out brand_group_to_execute_id/name (or maker_to_execute/maker_name) right after
  // _patchFormFromDetail() set them, before _prefillSelects() even reads them back out.
  onBusinessUnitChange(value: SelectOption): void {
    const next = (value?.id as BusinessUnit) ?? null;
    const current = this.form.get(PO_FIELD.BUSINESS_UNIT)?.value ?? null;
    this.form.get(PO_FIELD.BUSINESS_UNIT)?.setValue(next);
    if (next === current) {
      return;
    }
    this.form.get(PO_FIELD.MAKER_TO_EXECUTE)?.setValue(null);
    this.form.get(PO_FIELD.MAKER_NAME)?.setValue(null);
    this.form.get(PO_FIELD.BRAND_GROUP_TO_EXECUTE_ID)?.setValue(null);
    this.form.get(PO_FIELD.BRAND_GROUP_TO_EXECUTE_NAME)?.setValue(null);
    this.makerSelect?.clearSelection();
    this.brandGroupSelect?.clearSelection();
    // Whichever selector becomes visible gets a brand-new one-select/viewport — rebuild both data
    // sources fresh so the reappearing one never gets a disconnected (blank) instance.
    this.selectSources[PO_FIELD.MAKER_TO_EXECUTE] = this._buildMakerToExecuteSource();
    this.selectSources[PO_FIELD.BRAND_GROUP_TO_EXECUTE_ID] = this._buildBrandGroupToExecuteSource();
  }

  // one-select's setSelection() always emits selectionChanged (see
  // ngx-frontend/libs/ui/select/components/select.component.ts), so _prefillSelects() calling
  // brandGroupSelect.setSelection(...) to restore the display in edit mode re-enters this handler
  // with the *same* id already in the form. Without the no-op guard below, that reentrant call would
  // overwrite brand_group_to_execute_name with the compound "id - name" display label whenever
  // brandGroupNameById hasn't cached this id yet (e.g. it wasn't in the first fetched catalog page) —
  // and since _prefillSelects() rebuilds that label from whatever is currently in the form, each
  // edit/save cycle would prepend another "id - " onto the name.
  onBrandGroupChange(value: SelectOption): void {
    const nextId = value?.id ?? null;
    const currentId = this.form.get(PO_FIELD.BRAND_GROUP_TO_EXECUTE_ID)?.value ?? null;
    this.form.get(PO_FIELD.BRAND_GROUP_TO_EXECUTE_ID)?.setValue(nextId);
    if (nextId === currentId) {
      return;
    }
    this.form.get(PO_FIELD.BRAND_GROUP_TO_EXECUTE_NAME)?.setValue(
      value ? (this.brandGroupNameById.get(value.id) ?? value.name) : null
    );
  }

  isInvalid(key: string): boolean {
    if (!this.submitted) {
      return false;
    }
    const ctrl = this.form.get(key);
    return !ctrl?.value || !!ctrl.errors?.['required'];
  }
}
